import React, { useState } from 'react';
import PropTypes from 'prop-types';
import client from '../../../shared/api/client';
import { toastError } from '../../../shared/toastHelper';
import CheckCircle from './CheckCircle';

const PALETTE = {
  bar: 'var(--sl-bg-brand-primary)',
  track: 'var(--sl-border-brand)',
  fill: 'var(--sl-bg-brand-solid)',
  accent: 'var(--sl-text-brand)',
  border: 'var(--sl-border-brand)',
};

const SetupChecklistPanel = (props) => {
  const { teamId, teamMembershipId, doneCount, total, nextLabel, nextUrl, groups } = props;
  const [open, setOpen] = useState(!props.collapsed);
  const pct = total ? Math.round((doneCount / total) * 100) : 0;

  const toggle = () => {
    const next = !open;
    setOpen(next);
    client
      .patch(`/teams/${teamId}/team_memberships/${teamMembershipId}`, {
        team_membership: { setup_checklist_collapsed: !next },
      })
      .catch(() => {
        setOpen(!next);
        toastError('Could not save your preference. Please try again.');
      });
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggle();
    }
  };

  return (
    <div style={{ border: `1px solid ${PALETTE.border}`, borderRadius: 12, overflow: 'hidden', background: 'var(--sl-bg-primary)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '12px 20px', background: PALETTE.bar, flexWrap: 'wrap' }}>
        <div
          role="button"
          tabIndex={0}
          aria-expanded={open}
          aria-label="Toggle setup checklist"
          onClick={toggle}
          onKeyDown={onKeyDown}
          style={{ display: 'flex', alignItems: 'center', gap: 16, cursor: 'pointer', flex: '1 1 auto', minWidth: 0 }}
        >
          <span style={{ fontSize: 14, fontWeight: 600, color: PALETTE.accent, flexShrink: 0 }}>Setup checklist</span>
          <div style={{ flex: '1 1 120px', maxWidth: 240, minWidth: 80, height: 8, background: PALETTE.track, borderRadius: 9999, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${pct}%`, background: PALETTE.fill, borderRadius: 9999, transition: 'width .3s ease' }} />
          </div>
          <span style={{ fontSize: 12, color: PALETTE.accent, whiteSpace: 'nowrap' }}>{doneCount} of {total} complete</span>
          <span aria-hidden="true" style={{ color: PALETTE.accent, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }}>▾</span>
        </div>
        <a
          href={nextUrl}
          style={{ marginLeft: 'auto', padding: '8px 12px', background: PALETTE.fill, color: '#FFF', borderRadius: 8, fontSize: 14, fontWeight: 600, whiteSpace: 'nowrap' }}
        >
          Next: {nextLabel}
        </a>
      </div>
      {open && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0 24px', borderTop: `1px solid ${PALETTE.border}`, padding: '12px 12px 16px' }}>
          {groups.map((group) => (
            <div key={group.name} style={{ padding: '0 8px' }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--sl-text-primary)', marginBottom: 8 }}>
                {group.name}{' '}
                <span style={{ color: PALETTE.accent, fontWeight: 400 }}>
                  {group.items.filter((item) => item.done).length}/{group.items.length}
                </span>
              </div>
              {group.items.map((item) => (
                <a
                  key={item.key}
                  href={item.nextUrl}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', color: item.done ? 'var(--sl-text-secondary)' : PALETTE.accent, textDecoration: item.done ? 'line-through' : 'none' }}
                >
                  <CheckCircle done={item.done} />
                  <span style={{ fontSize: 13 }}>{item.label}</span>
                </a>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

SetupChecklistPanel.propTypes = {
  teamId: PropTypes.number.isRequired,
  teamMembershipId: PropTypes.number.isRequired,
  collapsed: PropTypes.bool,
  doneCount: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  nextLabel: PropTypes.string,
  nextUrl: PropTypes.string,
  groups: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      items: PropTypes.arrayOf(
        PropTypes.shape({
          key: PropTypes.string.isRequired,
          label: PropTypes.string.isRequired,
          done: PropTypes.bool,
          nextUrl: PropTypes.string,
        })
      ).isRequired,
    })
  ).isRequired,
};

export default SetupChecklistPanel;
